import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { HoverReveal } from '../components/HoverReveal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { DocumentUpload } from '../components/DocumentUpload';
import { CreateTicketModal } from '../components/CreateTicketModal';
import { FlowAttachInput, FlowAttachmentsView } from '../components/FlowAttachments';
import { InlineStatusEditor } from '../components/InlineStatusEditor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Phone,
  Mail,
  Video,
  FileText,
  Trash2,
  CheckCircle2,
  Zap,
  Plus,
  Ticket,
  Clock,
  Paperclip,
  Copy,
  AlertTriangle,
  Flame,
  Sun,
  BatteryCharging,
  Tv,
  Shield,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import { toast } from 'sonner';
import { ClientPageButton, EmpresaLink } from '../components/client360/c360Shared';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COMPANY_NAMES = {
  edp: 'EDP',
  galp: 'Galp',
  endesa: 'Endesa',
  iberdrola: 'Iberdrola',
  gold: 'Gold Energy',
  repsol: 'Repsol',
  yes: 'Yes',
  meo_energia: 'Meo Energia',
  nos: 'NOS',
  meo: 'MEO',
  vodafone: 'Vodafone',
  nos_securitas: 'NOS Securitas',
  prosegur: 'Prosegur'
};

const TICKET_TYPE_LABELS = {
  chamada_agendada: 'Novo Pedido de Chamada Agendada',
  chamada_imediata: 'Novo Pedido de Chamada Imediata',
  tratamento_pendencia: 'Tratamento Pendência',
  pedido_anulacao: 'Pedido Anulação',
};

const STATUSES = [
  { value: 'pedido_chamada', label: 'Pedido de Chamada' },
  { value: 'validacao_documentos', label: 'Validação de Documentos' },
  { value: 'chamada_efectuada', label: 'Chamada Efectuada' },
  { value: 'esclarecimentos', label: 'Esclarecimentos' },
  { value: 'pendente_documentos', label: 'Pendente de Documentos' },
  { value: 'anulado_comercializadora', label: 'Anulado pela Comercializadora' },
  { value: 'registado_comercializadora', label: 'Registado na Comercializadora' },
  { value: 'anulado', label: 'Anulado' },
  { value: 'baixa', label: 'Baixa' },
  { value: 'concluido', label: 'Concluído' },
];

const activityIcons = {
  email: Mail,
  chamada: Phone,
  reuniao: Video,
  nota: FileText,
  flow_comment: FileText,
  ticket: FileText,
  ficheiro_substituido: FileText,
  estado: CheckCircle2,
};

// Border-left color for activity entries based on the related contract status
const STATUS_FLOW_BORDER = {
  pedido_chamada: 'border-blue-300',
  validacao_documentos: 'border-cyan-300',
  chamada_efectuada: 'border-purple-300',
  nao_atende: 'border-rose-300',
  esclarecimentos: 'border-amber-400',
  esclarecimentos_respondidos: 'border-sky-300',
  pendente_documentos: 'border-yellow-400',
  registado_comercializadora: 'border-teal-300',
  anulado_comercializadora: 'border-orange-400',
  anulado: 'border-red-400',
  baixa: 'border-amber-700',
  concluido: 'border-[#B8E02A]',};

export const DUPLICATE_CATEGORIES = [
  {
    key: 'energias', label: 'Luz e Gas', icon: Flame, iconColor: 'text-orange-500', bgColor: 'bg-orange-50',
    companies: [
      { key: 'edp', name: 'EDP' }, { key: 'galp', name: 'Galp' }, { key: 'endesa', name: 'Endesa' },
      { key: 'iberdrola', name: 'Iberdrola' }, { key: 'gold', name: 'Gold Energy' },
      { key: 'repsol', name: 'Repsol' }, { key: 'yes', name: 'Yes' },
      { key: 'meo_energia', name: 'Meo Energia' },
    ]
  },
  {
    key: 'solar', label: 'Solar', icon: Sun, iconColor: 'text-amber-500', bgColor: 'bg-amber-50',
    companies: [{ key: 'edp', name: 'EDP' }, { key: 'galp', name: 'Galp' }, { key: 'iberdrola', name: 'Iberdrola' }]
  },
  {
    key: 'carregamento', label: 'Carregamento', icon: BatteryCharging, iconColor: 'text-green-500', bgColor: 'bg-green-50',
    companies: [{ key: 'edp', name: 'EDP' }, { key: 'galp', name: 'Galp' }, { key: 'iberdrola', name: 'Iberdrola' }]
  },
  {
    key: 'telecomunicacoes', label: 'Telecomunicacoes', icon: Tv, iconColor: 'text-purple-500', bgColor: 'bg-purple-50',
    companies: [{ key: 'nos', name: 'NOS' }, { key: 'meo', name: 'MEO' }, { key: 'vodafone', name: 'Vodafone' }]
  },
  {
    key: 'alarmes', label: 'Alarmes', icon: Shield, iconColor: 'text-red-500', bgColor: 'bg-red-50',
    companies: [{ key: 'nos_securitas', name: 'NOS Securitas' }, { key: 'prosegur', name: 'Prosegur' }]
  },
];

export const ContractDetail = () => {
  const { category, company, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isManager, isSuperAdmin, previewPartner, hasPerm } = useAuth();
  
  const companyName = COMPANY_NAMES[company] || company?.toUpperCase();

  // Excel export columns whitelist — when active, hides sensitive blocks in the detail view.
  // super_admin/gestor NEVER restricted (unless previewing as partner).
  const columnWhitelist = React.useMemo(() => {
    if (['super_admin', 'gestor'].includes(user?.role) && !previewPartner) return null;
    const perms = previewPartner?.permissions || user?.partner_permissions || {};
    if (perms.excel_export_restrito === true && Array.isArray(perms.excel_export_columns) && perms.excel_export_columns.length > 0) {
      return new Set(perms.excel_export_columns);
    }
    return null;
  }, [user, previewPartner]);
  const canSee = (keys) => {
    if (!columnWhitelist) return true;
    const list = Array.isArray(keys) ? keys : [keys];
    return list.some(k => columnWhitelist.has(k));
  };
  
  const [contract, setContract] = useState(null);
  const [activities, setActivities] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [activeTab, setActiveTab] = useState('fluxo');
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  // Corrigir Comercializadora dialog
  const [fixCompanyOpen, setFixCompanyOpen] = useState(false);
  const [fixCompany, setFixCompany] = useState('');
  const [fixComercAnt, setFixComercAnt] = useState('');
  const [fixMotivo, setFixMotivo] = useState('');
  const [fixSaving, setFixSaving] = useState(false);
  const [fixCompanyOptions, setFixCompanyOptions] = useState([]);

  // Esclarecimentos workflow state
  const [respondEsclarecOpen, setRespondEsclarecOpen] = useState(false);
  const [respondEsclarecText, setRespondEsclarecText] = useState('');
  const [respondingEsclarec, setRespondingEsclarec] = useState(false);
  const [rejectEsclarecOpen, setRejectEsclarecOpen] = useState(false);
  const [rejectEsclarecText, setRejectEsclarecText] = useState('');
  const [validatingEsclarec, setValidatingEsclarec] = useState(false);
  const [esclarecLocallyValidated, setEsclarecLocallyValidated] = useState(false);

  const handleRespondEsclarecimentos = async () => {
    const txt = respondEsclarecText.trim();
    if (txt.length < 10) {
      toast.error('Resposta obrigatória (mínimo 10 caracteres)');
      return;
    }
    setRespondingEsclarec(true);
    try {
      await axios.post(`${API_URL}/contracts/${id}/respond-esclarecimentos`, { notes: txt });
      toast.success('Resposta enviada — gestor notificado');
      setRespondEsclarecOpen(false);
      setRespondEsclarecText('');
      fetchContract();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro');
    } finally {
      setRespondingEsclarec(false);
    }
  };

  const handleValidateEsclarecimentos = async (action, reason) => {
    if (action === 'reject') {
      const txt = (reason || rejectEsclarecText || '').trim();
      if (txt.length < 10) {
        toast.error('Motivo obrigatório (mínimo 10 caracteres)');
        return;
      }
    }
    // Hard guard: se já validado, ignora clicks adicionais
    if (action === 'validate' && (contract?.esclarecimentos_validated_at || esclarecLocallyValidated)) {
      return;
    }
    // Lock optimista — botão muda imediatamente
    if (action === 'validate') {
      setEsclarecLocallyValidated(true);
    }
    setValidatingEsclarec(true);
    try {
      const body = { action };
      if (action === 'reject') body.notes = (reason || rejectEsclarecText || '').trim();
      const res = await axios.post(`${API_URL}/contracts/${id}/validate-esclarecimentos`, body);
      if (res.data?.already_validated) {
        toast.info('Já estava validado anteriormente');
      } else {
        toast.success(action === 'validate' ? 'Esclarecimentos validados' : 'Pedido de mais informação enviado');
      }
      setRejectEsclarecOpen(false);
      setRejectEsclarecText('');
      fetchContract();
    } catch (err) {
      // Se foi erro DURO no validate, desbloqueia (utilizador pode tentar de novo)
      if (action === 'validate') {
        setEsclarecLocallyValidated(false);
      }
      toast.error(err?.response?.data?.detail || 'Erro');
    } finally {
      setValidatingEsclarec(false);
    }
  };

  // Duplicate modal
  const [dupModalOpen, setDupModalOpen] = useState(false);
  const [dupStep, setDupStep] = useState('category'); // 'category' or 'company'
  const [dupCategory, setDupCategory] = useState(null);
  const [dupData, setDupData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  // Reset local validate-lock sempre que o contrato muda
  useEffect(() => {
    setEsclarecLocallyValidated(false);
  }, [contract?.id, contract?.esclarecimentos_validated_at]);
  
  // Global refresh button
  useEffect(() => {
    const h = () => fetchData();
    window.addEventListener('crm:refresh', h);
    return () => window.removeEventListener('crm:refresh', h);
  }, [id]);

  const fetchData = async () => {
    try {
      const [contractRes, flowRes, ticketsRes] = await Promise.all([
        axios.get(`${API_URL}/contracts/${id}`),
        axios.get(`${API_URL}/contracts/${id}/flow`),
        axios.get(`${API_URL}/tickets?contract_id=${id}`)
      ]);
      setContract(contractRes.data);
      setActivities(flowRes.data);
      setTickets(ticketsRes.data);
    } catch (error) {
      console.error('Error fetching contract:', error);
      toast.error('Erro ao carregar contrato');
      navigate(`/contracts/${category}/${company}`);
    } finally {
      setLoading(false);
    }
  };

  const [showAnularModal, setShowAnularModal] = useState(false);
  const [motivoAnulacao, setMotivoAnulacao] = useState('');
  const [anularFiles, setAnularFiles] = useState([]);
  const [noteFiles, setNoteFiles] = useState([]);

  const handleStatusChange = async (newStatus) => {
    // If cancelling, show the modal to require a reason
    if (newStatus === 'anulado' || newStatus === 'anulado_comercializadora' || newStatus === 'baixa') {
      setShowAnularModal(newStatus);
      return;
    }
    try {
      await axios.put(`${API_URL}/contracts/${id}`, { status: newStatus });
      setContract({ ...contract, status: newStatus });
      toast.success('Estado atualizado');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao atualizar estado';
      toast.error(message);
    }
  };

  const handleAnular = async () => {
    if (!motivoAnulacao.trim()) {
      toast.error('Indique o motivo da anulacao');
      return;
    }
    try {
      await axios.put(`${API_URL}/contracts/${id}`, { status: showAnularModal, motivo_anulacao: motivoAnulacao.trim() });
      // If attachments present, also post a flow comment with them
      if (anularFiles.length > 0) {
        const fd = new FormData();
        fd.append('content', `Anexos da anulação: ${motivoAnulacao.trim()}`);
        anularFiles.forEach(f => fd.append('files', f));
        try { await axios.post(`${API_URL}/contracts/${id}/flow-comment`, fd); } catch (e) { console.error(e); }
      }
      setContract({ ...contract, status: showAnularModal, motivo_anulacao: motivoAnulacao.trim() });
      toast.success('Contrato anulado');
      setShowAnularModal(false);
      setMotivoAnulacao('');
      setAnularFiles([]);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao anular contrato';
      toast.error(message);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() && noteFiles.length === 0) return;
    setAddingNote(true);
    try {
      const fd = new FormData();
      if (newNote.trim()) fd.append('content', newNote.trim());
      noteFiles.forEach(f => fd.append('files', f));
      await axios.post(`${API_URL}/contracts/${id}/flow-comment`, fd);
      setNewNote('');
      setNoteFiles([]);
      toast.success('Comentário adicionado');
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao adicionar comentário');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja eliminar este contrato?')) return;
    
    try {
      await axios.delete(`${API_URL}/contracts/${id}`);
      toast.success('Contrato eliminado');
      navigate(`/contracts/${category}/${company}`);
    } catch (error) {
      toast.error('Erro ao eliminar contrato');
    }
  };

  const handleDuplicate = async () => {
    try {
      const res = await axios.post(`${API_URL}/contracts/${id}/duplicate`);
      setDupData(res.data);
      // Pre-select the same category
      const srcCat = res.data.source_category || category;
      const matchedCat = DUPLICATE_CATEGORIES.find(c => c.key === srcCat);
      setDupCategory(matchedCat || null);
      setDupStep(matchedCat ? 'company' : 'category');
      setDupModalOpen(true);
    } catch (error) {
      toast.error('Erro ao duplicar');
    }
  };

  // Open the "Corrigir Comercializadora" dialog: prefill values + load company options
  useEffect(() => {
    if (!fixCompanyOpen) return;
    setFixCompany(contract?.company || '');
    setFixComercAnt((contract?.comercializadora_anterior || '').toLowerCase());
    setFixMotivo('');
    const cat = (() => {
      const c = contract?.service_category || category;
      return (c === 'energias' || c === 'luz') ? 'luz_gas' : c;
    })();
    axios.get(`${API_URL}/config/companies/${cat}`)
      .then(res => {
        const map = res.data || {};
        const list = Object.entries(map).map(([slug, info]) => ({ slug, name: info?.name || slug }));
        setFixCompanyOptions(list);
      })
      .catch(() => setFixCompanyOptions([]));
  }, [fixCompanyOpen, contract, category]);

  const handleFixCompanySubmit = async () => {
    if (fixMotivo.trim().length < 10) {
      toast.error('Motivo deve ter pelo menos 10 caracteres');
      return;
    }
    setFixSaving(true);
    try {
      const res = await axios.post(`${API_URL}/contracts/${id}/corrigir-comercializadora`, {
        company: fixCompany || null,
        comercializadora_anterior: fixComercAnt || null,
        motivo: fixMotivo.trim(),
      });
      toast.success('Comercializadora corrigida — registado no Fluxo');
      setFixCompanyOpen(false);
      const newCompany = res.data.company;
      // If company changed, navigate to the new URL (route param)
      if (newCompany && newCompany !== company) {
        navigate(`/contracts/${category}/${newCompany}/${id}`, { replace: true });
      } else {
        fetchData();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao corrigir');
    } finally {
      setFixSaving(false);
    }
  };

  const handleDupSelectCategory = (cat) => {
    setDupCategory(cat);
    setDupStep('company');
  };

  const handleDupSelectCompany = (comp) => {
    if (!dupData || !dupCategory) return;
    const params = new URLSearchParams(dupData);
    params.delete('source_category');
    params.delete('source_company');
    setDupModalOpen(false);
    navigate(`/contracts/${dupCategory.key}/${comp.key}/new?duplicate=true&${params.toString()}`);
    toast.success('Dados copiados — escolha a empresa e ajuste o que precisar');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), "dd MMM yyyy 'às' HH:mm", { locale: pt });
    } catch {
      return dateStr;
    }
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd/MM/yyyy', { locale: pt });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  if (!contract) return null;

  return (
    <Layout>
      <div className="space-y-6" data-testid="contract-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                const returnTo = location.state?.returnTo;
                if (returnTo) navigate(returnTo);
                else if (window.history.length > 1) navigate(-1);
                else navigate(`/contracts/${category}/${company}`);
              }}
              className="rounded-full"
              data-testid="contract-detail-back"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <Zap className="w-4 h-4 text-[#4DD4E8]" />
                <EmpresaLink company={company} testId="contract-detail-empresa-360" />
                <span>›</span>
                <span className="text-[#2D3E6F] font-medium">{contract.codigo}</span>
              </div>
              <h1 className="text-xl font-bold text-slate-900 font-outfit" data-testid="contract-name">
                {contract.nome_cliente}
              </h1>
              <ClientPageButton nif={contract.nif} name={contract.nome_cliente} contractId={contract.id} testId="contract-detail-client-360" />
              {contract.nif_divergente && !contract.nif_divergencia_resolvida && (
                <span
                  className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold border border-red-300"
                  data-testid="contract-nif-divergente-badge"
                  title="O NIF deste contrato está registado noutro cliente. Verificação pendente."
                >
                  <AlertTriangle className="w-3 h-3" />
                  NIF DIVERGENTE
                </span>
              )}
              {contract.nif_divergente && contract.nif_divergencia_resolvida && (
                <span
                  className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold border border-emerald-300"
                  data-testid="contract-nif-divergencia-resolvida-badge"
                  title="Divergência de NIF verificada e resolvida."
                >
                  <CheckCircle2 className="w-3 h-3" />
                  Divergência verificada
                </span>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="rounded-full">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="rounded-full">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tipo de Segmento / Autorizações */}
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-semibold">Tipo de Segmento / Autorizações</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Tipo Segmento</p>
                    <p className="font-medium capitalize">{contract.tipo_segmento}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Tipo Produto</p>
                    <p className="font-medium capitalize">{contract.tipo_produto?.replace('_', ' + ')}</p>
                  </div>
                  {contract.potencia && (
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Potência Contratada</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${parseFloat(contract.potencia) < 3.45 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`} title={parseFloat(contract.potencia) < 3.45 ? 'Inferior a 3,45 kVA — não comissionada' : 'Comissionável'}>
                        {parseFloat(contract.potencia) < 3.45 && <span className="mr-1">⚠️</span>}{contract.potencia} kVA
                      </span>
                    </div>
                  )}
                  {contract.escalao_gas && (
                    <div data-testid="contract-escalao-gas">
                      <p className="text-sm text-slate-500 mb-1">Escalão de Gás</p>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">Escalão {contract.escalao_gas}</span>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Localidade</p>
                    <p className="font-medium">{contract.localidade || 'Vazio'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Indoor</p>
                    <p className="font-medium capitalize">{contract.indoor}</p>
                  </div>
                </div>

                {/* CPE/CUI - Only for energy/solar/charging */}
                {['energias', 'luz_gas', 'solar', 'carregamento'].includes(category) && canSee(['cpe', 'cui']) && (
                <div className="grid grid-cols-3 gap-6 mt-6 pt-6 border-t border-slate-100">
                  {canSee('cpe') && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">CPE</p>
                    <p className="font-mono font-medium">
                      {(contract.cpe || '').includes('*')
                        ? <HoverReveal contractId={contract.id} field="cpe">{contract.cpe}</HoverReveal>
                        : (contract.cpe || '-')}
                    </p>
                  </div>
                  )}
                  {canSee('cui') && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">CUI</p>
                    <p className="font-mono font-medium">
                      {(contract.cui || '').includes('*')
                        ? <HoverReveal contractId={contract.id} field="cui">{contract.cui}</HoverReveal>
                        : (contract.cui || '-')}
                    </p>
                  </div>
                  )}
                </div>
                )}

                {(contract.nome_registo_cu || contract.codigo_registo_cu) && (
                  <div className="grid grid-cols-2 gap-6 mt-6 pt-6 border-t border-slate-100">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Parceiro (Nome Registo C.U.)</p>
                      <p className="font-medium">{contract.nome_registo_cu || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Código Registo C.U.</p>
                      <p className="font-mono font-medium">{contract.codigo_registo_cu || '-'}</p>
                    </div>
                  </div>
                )}

                {/* Luz/Gas checkboxes - Only for energy */}
                {['energias', 'luz_gas'].includes(category) && (
                <div className="grid grid-cols-3 gap-6 mt-6 pt-6 border-t border-slate-100">
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Luz</p>
                    <div className="space-y-1">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={contract.luz_mc} readOnly className="rounded text-[#4DD4E8]" />
                        M.C
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={contract.luz_at} readOnly className="rounded" />
                        A.T
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={contract.luz_ed} readOnly className="rounded" />
                        E.D
                      </label>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Gás</p>
                    <div className="space-y-1">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={contract.gas_mc} readOnly className="rounded text-[#4DD4E8]" />
                        M.C
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={contract.gas_at} readOnly className="rounded" />
                        A.T
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={contract.gas_ed} readOnly className="rounded" />
                        E.D
                      </label>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Cartão Cidadão</p>
                    <p className="font-medium capitalize">{contract.cartao_cidadao}</p>
                  </div>
                </div>
                )}
              </CardContent>
            </Card>

            {/* Estado da Venda */}
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-semibold">Estado da Venda</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Estado</p>
                    <InlineStatusEditor
                      contract={contract}
                      onUpdated={(_id, newStatus) => {
                        setContract(prev => prev ? { ...prev, status: newStatus } : prev);
                        fetchData();
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Data Assinatura</p>
                      <p className="font-medium">{formatDateShort(contract.data_assinatura)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Agendamento</p>
                      <p className="font-medium">{formatDateShort(contract.agendamento)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Informacao Financeira */}
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-semibold">Informacao Financeira</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Valor Comissao</p>
                    <p className="font-mono font-medium text-lg">
                      {contract.valor_comissao != null ? `${Number(contract.valor_comissao).toFixed(2)} EUR` : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Estado Pagamento</p>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      contract.estado_pagamento === 'pago' ? 'bg-emerald-100 text-emerald-700' :
                      contract.estado_pagamento === 'chargeback' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {contract.estado_pagamento === 'pago' ? 'Pago' :
                       contract.estado_pagamento === 'chargeback' ? 'Chargeback' : 'Pendente'}
                    </span>
                  </div>
                </div>
                {/* Motivo do estado actual + Resposta do comercial (Esclarecimentos workflow) */}
                {(contract.motivo_estado || contract.esclarecimentos_response) && contract.status !== 'anulado' && (
                  <div className="mt-4 space-y-2" data-testid="motivo-estado-box">
                    {contract.motivo_estado && (
                      <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                        <p className="text-xs font-medium text-amber-700 mb-1">Motivo — {STATUSES.find(s => s.value === (contract.status === 'esclarecimentos_respondidos' ? 'esclarecimentos' : contract.status))?.label || contract.status}</p>
                        <p className="text-sm text-amber-900 whitespace-pre-wrap">{contract.motivo_estado}</p>
                      </div>
                    )}
                    {contract.esclarecimentos_response && (
                      <div className="p-3 rounded-lg bg-sky-50 border border-sky-200" data-testid="esclarecimentos-response-box">
                        <p className="text-xs font-medium text-sky-700 mb-1">
                          Resposta — {contract.esclarecimentos_response_by_name || 'Comercial'}
                          {contract.esclarecimentos_response_at && (
                            <span className="text-sky-500 ml-2">{new Date(contract.esclarecimentos_response_at).toLocaleString('pt-PT')}</span>
                          )}
                        </p>
                        <p className="text-sm text-sky-900 whitespace-pre-wrap">{contract.esclarecimentos_response}</p>
                      </div>
                    )}
                    {/* Action buttons */}
                    {contract.status === 'esclarecimentos' && contract.assigned_to === user?.id && (
                      <Button
                        size="sm"
                        className="bg-sky-600 hover:bg-sky-700 gap-2"
                        onClick={() => setRespondEsclarecOpen(true)}
                        data-testid="btn-respond-esclarecimentos"
                      >
                        Responder a Esclarecimentos
                      </Button>
                    )}
                    {(contract.status === 'esclarecimentos_respondidos' ||
                      (contract.esclarecimentos_response && !contract.esclarecimentos_validated_at)) &&
                      ['super_admin', 'gestor', 'admin'].includes(user?.role) && (
                      <div className="flex gap-2 items-center">
                        {(contract.esclarecimentos_validated_at || esclarecLocallyValidated) ? (
                          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-100 border border-emerald-300 text-emerald-800 text-sm font-semibold" data-testid="esclarec-validated-pill">
                            <CheckCircle2 className="w-4 h-4" /> Resposta Validada
                            {contract.esclarecimentos_validated_at && (
                              <span className="text-[11px] font-normal text-emerald-700">
                                · {new Date(contract.esclarecimentos_validated_at).toLocaleString('pt-PT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                              onClick={() => handleValidateEsclarecimentos('validate')}
                              disabled={validatingEsclarec || esclarecLocallyValidated}
                              data-testid="btn-validate-esclarecimentos"
                            >
                              {validatingEsclarec ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : '✓'} Validar Resposta
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-rose-300 text-rose-700 hover:bg-rose-50 gap-2"
                              onClick={() => setRejectEsclarecOpen(true)}
                              disabled={validatingEsclarec || esclarecLocallyValidated}
                              data-testid="btn-reject-esclarecimentos"
                            >
                              Pedir mais info
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {contract.motivo_anulacao && (
                  <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-xs font-medium text-red-700 mb-1">Motivo da Anulacao</p>
                    <p className="text-sm text-red-800">{contract.motivo_anulacao}</p>
                    {contract.anulado_via_ticket_id && (
                      <Link
                        to={`/tickets/${contract.anulado_via_ticket_id}`}
                        className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-red-700 hover:text-red-900 hover:underline"
                        data-testid="anulado-via-ticket-link"
                      >
                        <Ticket className="w-3 h-3" />
                        Anulado via Ticket
                      </Link>
                    )}
                  </div>
                )}
                {contract.lead_id && (
                  <div className="mt-4 p-3 rounded-lg bg-teal-50 border border-teal-200" data-testid="lead-origin-badge">
                    <p className="text-xs font-medium text-teal-700 mb-1">Origem: Lead de Angariador</p>
                    <p className="text-sm text-teal-800">{contract.angariador_name || 'Angariador'}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Dados Cliente */}
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-semibold">Dados Cliente / Empresa</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Nome Cliente / Empresa</p>
                    <p className="font-medium">{contract.nome_cliente}</p>
                  </div>
                  {canSee('nif') && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">NIF</p>
                    <p className="font-mono font-medium">
                      {(contract.nif || '').includes('*')
                        ? <HoverReveal contractId={contract.id} field="nif">{contract.nif}</HoverReveal>
                        : (contract.nif || '-')}
                    </p>
                  </div>
                  )}
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Comercializadora Anterior</p>
                    <p className="font-medium" data-testid="comercializadora-anterior-display">
                      {(() => {
                        const ca = (contract.comercializadora_anterior || '').toLowerCase();
                        if (!ca) return '-';
                        if (ca === 'primeira_contratacao') return 'Primeira contratação';
                        if (ca === 'outra') return 'Não sabido / Outra';
                        return ca.charAt(0).toUpperCase() + ca.slice(1);
                      })()}
                    </p>
                  </div>
                  {canSee('telefones_str') && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Telefone(s)</p>
                    {contract.telefones?.length > 0 ? (
                      <div className="space-y-1">
                        {contract.telefones.map((tel, i) => (
                          <p key={i} className="font-medium text-[#4DD4E8]">
                            <span className="text-slate-400 text-sm capitalize">{tel.tipo}: </span>
                            {(tel.numero || '').includes('*')
                              ? <HoverReveal contractId={contract.id} field="telefones">{tel.numero}</HoverReveal>
                              : tel.numero}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="font-medium">-</p>
                    )}
                  </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Morada de Fornecimento */}
            {canSee(['morada_fornecimento', 'nr_porta_fornecimento', 'andar_fornecimento', 'codigo_postal_fornecimento', 'localidade']) && (
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-semibold">Morada de Fornecimento</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <p className="text-sm text-slate-500 mb-1">Morada</p>
                    <p className="font-medium">{contract.morada_fornecimento || contract.morada || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Nº Porta</p>
                    <p className="font-medium">{contract.nr_porta_fornecimento || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Andar</p>
                    <p className="font-medium">{contract.andar_fornecimento || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Lado</p>
                    <p className="font-medium" data-testid="contract-lado-fornecimento">{contract.lado_fornecimento || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Código Postal</p>
                    <p className="font-mono font-medium">{contract.codigo_postal_fornecimento || contract.codigo_postal || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Localidade</p>
                    <p className="font-medium">{contract.localidade_fornecimento || contract.localidade || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Morada de Faturação - shown when different from Fornecimento */}
            {(() => {
              if (!canSee(['morada_fornecimento', 'codigo_postal_fornecimento', 'localidade'])) return null;
              const fMorada = (contract.morada_faturacao || '').trim();
              const sMorada = (contract.morada_fornecimento || contract.morada || '').trim();
              const fCp = (contract.codigo_postal_faturacao || '').trim();
              const sCp = (contract.codigo_postal_fornecimento || contract.codigo_postal || '').trim();
              const isDifferent = fMorada && (fMorada !== sMorada || fCp !== sCp);
              if (!isDifferent) return null;
              return (
                <Card className="rounded-2xl border-slate-100">
                  <CardHeader className="border-b border-slate-100">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      Morada de Faturação
                      <span className="text-xs font-normal text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">diferente da fornecimento</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <p className="text-sm text-slate-500 mb-1">Morada</p>
                        <p className="font-medium">{contract.morada_faturacao || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Nº Porta</p>
                        <p className="font-medium">{contract.nr_porta_faturacao || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Andar</p>
                        <p className="font-medium">{contract.andar_faturacao || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Lado</p>
                        <p className="font-medium" data-testid="contract-lado-faturacao">{contract.lado_faturacao || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Código Postal</p>
                        <p className="font-mono font-medium">{contract.codigo_postal_faturacao || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Localidade</p>
                        <p className="font-medium">{contract.localidade_faturacao || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Dados Produto */}
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-semibold">Dados Produto</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Campanha</p>
                    <p className="font-medium">{contract.campanha || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">SVA</p>
                    <p className="font-medium capitalize">{contract.sva}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dados Faturação */}
            {canSee(['iban', 'fatura_eletronica', 'debito_direto', 'emails_str']) && (
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-semibold">Dados Faturação</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  {canSee('fatura_eletronica') && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Fatura Eletrónica</p>
                    <p className="font-medium capitalize">{contract.fatura_eletronica}</p>
                  </div>
                  )}
                  {canSee('emails_str') && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Email(s)</p>
                    {contract.emails?.length > 0 ? (
                      <div className="space-y-1">
                        {contract.emails.map((email, i) => (
                          <p key={i} className="font-medium text-[#4DD4E8]">
                            {(email || '').includes('*')
                              ? <HoverReveal contractId={contract.id} field="emails">{email}</HoverReveal>
                              : email}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="font-medium">-</p>
                    )}
                  </div>
                  )}
                  {canSee('debito_direto') && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Débito Direto</p>
                    <p className="font-medium capitalize">{contract.debito_direto}</p>
                  </div>
                  )}
                  {canSee('iban') && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">IBAN</p>
                    <p className="font-mono font-medium">
                      {(contract.iban || '').includes('*')
                        ? <HoverReveal contractId={contract.id} field="iban">{contract.iban}</HoverReveal>
                        : (contract.iban || '-')}
                    </p>
                  </div>
                  )}
                </div>
              </CardContent>
            </Card>
            )}

            {/* Documentos */}
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-semibold">Carregar Documentos</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <DocumentUpload contractId={contract?.id} />
              </CardContent>
            </Card>

            {/* Observações / Tipo de Pendência */}
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-semibold">Observações / Tipo de Pendência</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-sm">{contract.tipo_pendencia || 'Vazio'}</p>
              </CardContent>
            </Card>

            {/* Fluxo / Tickets Tabs */}
            <Card className="rounded-2xl border-slate-100" data-testid="fluxo-tickets-card">
              <div className="flex border-b border-slate-100">
                <button
                  onClick={() => setActiveTab('fluxo')}
                  className={`px-6 py-3 text-sm font-semibold transition-colors relative ${
                    activeTab === 'fluxo'
                      ? 'text-[#2D3E6F]'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
                  data-testid="tab-fluxo"
                >
                  Fluxo
                  {activeTab === 'fluxo' && (
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-[#2D3E6F] rounded-full" />
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('tickets')}
                  className={`px-6 py-3 text-sm font-semibold transition-colors relative ${
                    activeTab === 'tickets'
                      ? 'text-[#2D3E6F]'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
                  data-testid="tab-tickets"
                >
                  Tickets
                  {activeTab === 'tickets' && (
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-[#2D3E6F] rounded-full" />
                  )}
                </button>
              </div>

              <CardContent className="p-4">
                {activeTab === 'fluxo' ? (
                  <div className="space-y-4" data-testid="fluxo-content">
                    {/* Comment Input — Angariadores see attachments but cannot post */}
                    {user?.role !== 'angariador' ? (
                      <div className="flex flex-wrap gap-3">
                        <Textarea
                          placeholder="Escreva o seu comentário aqui (anexe imagens ou PDF)"
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          className="min-h-[60px] rounded-lg bg-slate-50 text-sm flex-1"
                          data-testid="fluxo-comment-input"
                        />
                        <div className="flex items-center gap-1 self-end">
                          <FlowAttachInput files={noteFiles} onChange={setNoteFiles} disabled={addingNote} />
                          <Button
                            onClick={handleAddNote}
                            disabled={(!newNote.trim() && noteFiles.length === 0) || addingNote}
                            size="sm"
                            className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full px-4"
                            data-testid="fluxo-comment-submit"
                          >
                            {addingNote ? '...' : 'Enviar'}
                          </Button>
                        </div>
                        {noteFiles.length > 0 && (
                          <div className="w-full flex flex-wrap gap-2">
                            {/* attached chips rendered by FlowAttachInput */}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-slate-400 italic px-2 py-1.5 bg-slate-50 rounded-lg" data-testid="fluxo-read-only-notice">
                        Vê o histórico do contrato. Como angariador, não podes adicionar comentários — fala com a tua chefia se for necessário.
                      </div>
                    )}

                    {/* Activity Flow */}
                    {activities.length > 0 ? (
                      <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
                        {activities.map((activity) => {
                          const Icon = activityIcons[activity.type] || FileText;
                          const isStateChange = activity.type === 'estado' && activity.new_status;
                          const borderColor = isStateChange
                            ? STATUS_FLOW_BORDER[activity.new_status] || 'border-slate-200'
                            : 'border-slate-200';
                          return (
                            <div
                              key={activity.id}
                              className={`flex items-start gap-3 text-sm border-l-4 ${borderColor} pl-4 py-2`}
                              data-testid={`activity-${activity.id}`}
                            >
                              <div className="flex-1">
                                {isStateChange && (
                                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                    <StatusBadge status={activity.new_status} size="lg" />
                                    {activity.old_status && (
                                      <>
                                        <span className="text-xs text-slate-400">de</span>
                                        <StatusBadge status={activity.old_status} size="xs" />
                                      </>
                                    )}
                                  </div>
                                )}
                                <p className="text-slate-600">
                                  <span className="inline-flex items-center gap-1 align-middle">
                                    <Icon className="w-3.5 h-3.5 text-slate-400" />
                                    <span className="font-medium text-slate-800">{activity.created_by_name || 'Sistema'}</span>
                                  </span>
                                  {activity.description ? <>{' '}{activity.description}</> : (activity.type === 'flow_comment' && activity.attachments?.length > 0 ? ' anexou ficheiros' : null)}
                                </p>
                                {activity.attachments?.length > 0 && (
                                  <FlowAttachmentsView attachments={activity.attachments} />
                                )}
                                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatDate(activity.created_at)}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 text-center py-6">Sem dados</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="tickets-content">
                    {/* Tickets Header */}
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-slate-700">Tickets</h3>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setTicketModalOpen(true)}
                          data-testid="create-ticket-from-contract"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Tickets List */}
                    {tickets.length > 0 ? (
                      <div className="space-y-2">
                        {tickets.map((ticket) => (
                          <div
                            key={ticket.id}
                            className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                            data-testid={`ticket-item-${ticket.id}`}
                            onClick={() => navigate(`/tickets/${ticket.id}`)}
                          >
                            <Ticket className="w-4 h-4 text-[#2D3E6F] flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-slate-700">
                                  {TICKET_TYPE_LABELS[ticket.tipo] || ticket.tipo}
                                </span>
                                <StatusBadge status={ticket.status} />
                                <StatusBadge status={ticket.priority} />
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5 truncate">
                                {ticket.description || 'Sem descricao'}
                              </p>
                              <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                                <span>{ticket.assigned_to_name || '-'}</span>
                                <span>{formatDate(ticket.created_at)}</span>
                                {ticket.file_count > 0 && (
                                  <span className="flex items-center gap-0.5">
                                    <Paperclip className="w-3 h-3" /> {ticket.file_count}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 text-center py-6">Sem dados</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Assigned User */}
            <Card className="rounded-2xl border-slate-100">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Utilizador atribuído</p>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="bg-[#FFB800] text-white text-xs">
                          {getInitials(contract.assigned_to_name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-[#4DD4E8] font-medium">{contract.assigned_to_name || '-'}</span>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Equipa</p>
                    <span className="text-[#4DD4E8]">{contract.team_name || '-'}</span>
                  </div>
                  
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Criado</p>
                    <p className="text-sm">{formatDate(contract.created_at)}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Modificado</p>
                    <p className="text-sm">{formatDate(contract.updated_at)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Activities Summary */}
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100 py-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold">Atividades</CardTitle>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Mail className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Phone className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Video className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                {activities.length > 0 ? (
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {activities.slice(0, 5).map((activity) => {
                      const Icon = activityIcons[activity.type] || FileText;
                      return (
                        <div key={activity.id} className="flex items-start gap-3 text-sm">
                          <Icon className="w-4 h-4 text-slate-400 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-slate-700">{activity.description}</p>
                            {activity.old_status && activity.new_status && (
                              <div className="flex items-center gap-1 mt-1">
                                <StatusBadge status={activity.old_status} className="text-[10px]" />
                                <span className="text-slate-400">→</span>
                                <StatusBadge status={activity.new_status} className="text-[10px]" />
                              </div>
                            )}
                            <p className="text-xs text-slate-400 mt-0.5">
                              {formatDate(activity.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">Sem dados</p>
                )}
              </CardContent>
            </Card>

            {/* History / Notes */}
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100 py-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold">Histórico</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                {activities.length > 0 ? (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {activities.slice(0, 3).map(a => (
                      <p key={a.id} className="text-xs text-slate-500">
                        {a.created_by_name}: {a.description}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-2">Sem dados</p>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex gap-2 flex-wrap">
              {(() => {
                // Edit access (expanded):
                // - super_admin & gestor: always
                // - admin/parceiro: only if partner permission "editar_contrato" is allowed
                //   (lead-generator-only partners like Credível have this disabled)
                // - other roles: blocked
                const canEdit =
                  isSuperAdmin() ||
                  user?.role === 'gestor' ||
                  (['admin', 'parceiro'].includes(user?.role) && hasPerm('editar_contrato'));
                if (!canEdit) return null;
                return (
                  <Button
                    variant="outline"
                    className="flex-1 rounded-full gap-2"
                    onClick={() => navigate(`/contracts/${category}/${company}/${id}/edit`, { state: { returnTo: location.state?.returnTo || (location.pathname + location.search) } })}
                    data-testid="edit-contract-button"
                  >
                    <FileText className="w-4 h-4" />
                    Editar
                  </Button>
                );
              })()}
              {(isSuperAdmin() || user?.role === 'gestor' || user?.role === 'admin') && (
              <Button
                variant="outline"
                className="flex-1 rounded-full gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={() => setFixCompanyOpen(true)}
                data-testid="fix-company-button"
                title="Corrigir comercializadora rapidamente"
              >
                <RefreshCw className="w-4 h-4" />
                Corrigir Comercializadora
              </Button>
              )}
              <Button
                variant="outline"
                className="flex-1 rounded-full gap-2"
                onClick={handleDuplicate}
                data-testid="duplicate-contract-button"
              >
                <Copy className="w-4 h-4" />
                Duplicar
              </Button>
              {isSuperAdmin() && (
              <Button
                variant="outline"
                className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleDelete}
                data-testid="delete-contract-button"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Ticket Modal */}
      <CreateTicketModal
        open={ticketModalOpen}
        onOpenChange={setTicketModalOpen}
        contract={contract}
        onCreated={fetchData}
      />

      {/* Anular Modal */}
      <Dialog open={!!showAnularModal} onOpenChange={(v) => { if (!v) { setShowAnularModal(false); setMotivoAnulacao(''); setAnularFiles([]); } }}>
        <DialogContent className="sm:max-w-md" data-testid="anular-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Anular Contrato
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <p className="text-sm text-slate-600">
              Tem a certeza que deseja anular o contrato <strong>#{contract?.codigo}</strong> de <strong>{contract?.nome_cliente}</strong>?
            </p>
            <div>
              <Label className="text-sm font-medium text-slate-700">Motivo da Anulacao *</Label>
              <Textarea
                value={motivoAnulacao}
                onChange={(e) => setMotivoAnulacao(e.target.value)}
                placeholder="Indique o motivo da anulacao..."
                className="mt-1"
                rows={3}
                data-testid="motivo-anulacao-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 flex items-center justify-between">
                <span>Anexos (opcional)</span>
                <FlowAttachInput files={anularFiles} onChange={setAnularFiles} />
              </Label>
              <p className="text-xs text-slate-400 mt-1">Imagens ou PDFs como prova/contexto da anulação. Vão para o Fluxo do contrato.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAnularModal(false); setMotivoAnulacao(''); setAnularFiles([]); }}>Cancelar</Button>
            <Button
              variant="destructive"
              onClick={handleAnular}
              disabled={!motivoAnulacao.trim()}
              data-testid="confirm-anular-btn"
            >
              Confirmar Anulacao
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Modal - Category/Company Picker */}
      <Dialog open={dupModalOpen} onOpenChange={(v) => { if (!v) { setDupModalOpen(false); setDupStep('category'); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="duplicate-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5 text-[#2D3E6F]" />
              {dupStep === 'category' ? 'Duplicar — Escolher Servico' : 'Duplicar — Escolher Empresa'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-3">
            {dupStep === 'category' ? (
              <div className="grid grid-cols-2 gap-3">
                {DUPLICATE_CATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.key}
                      onClick={() => handleDupSelectCategory(cat)}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all hover:shadow-md ${
                        dupData?.source_category === cat.key ? 'border-[#2D3E6F] bg-blue-50/50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`dup-cat-${cat.key}`}
                    >
                      <div className={`w-10 h-10 rounded-lg ${cat.bgColor} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${cat.iconColor}`} />
                      </div>
                      <span className="font-medium text-sm text-slate-700">{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={() => setDupStep('category')}
                  className="text-xs text-[#4DD4E8] hover:underline mb-2"
                >
                  ← Voltar aos servicos
                </button>
                <div className="grid grid-cols-2 gap-3">
                  {dupCategory?.companies.map((comp) => (
                    <button
                      key={comp.key}
                      onClick={() => handleDupSelectCompany(comp)}
                      className={`p-4 rounded-xl border-2 text-center font-semibold transition-all hover:shadow-md ${
                        dupData?.source_company === comp.key ? 'border-[#2D3E6F] bg-blue-50/50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`dup-comp-${comp.key}`}
                    >
                      {comp.name}
                      {dupData?.source_company === comp.key && (
                        <span className="block text-xs font-normal text-slate-400 mt-1">(original)</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Respond to Esclarecimentos */}
      <Dialog open={respondEsclarecOpen} onOpenChange={setRespondEsclarecOpen}>
        <DialogContent data-testid="respond-esclarec-dialog">
          <DialogHeader>
            <DialogTitle>Responder a Esclarecimentos</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {contract?.motivo_estado && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-xs font-medium text-amber-700 mb-1">Pedido original:</p>
                <p className="text-sm text-amber-900 whitespace-pre-wrap">{contract.motivo_estado}</p>
              </div>
            )}
            <div>
              <Label>A tua resposta <span className="text-rose-500">*</span></Label>
              <Textarea
                value={respondEsclarecText}
                onChange={(e) => setRespondEsclarecText(e.target.value)}
                rows={5}
                placeholder="Explica o que verificaste, o que comunicaste ao cliente, e o que vais corrigir..."
                data-testid="respond-esclarec-input"
              />
              <p className="text-xs text-slate-400 mt-1">Mínimo 10 caracteres ({respondEsclarecText.trim().length}/10)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRespondEsclarecOpen(false)} disabled={respondingEsclarec}>Cancelar</Button>
            <Button
              onClick={handleRespondEsclarecimentos}
              disabled={respondingEsclarec || respondEsclarecText.trim().length < 10}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid="confirm-respond-esclarec"
            >
              {respondingEsclarec ? 'A enviar...' : 'Enviar Resposta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject (request more info) Esclarecimentos */}
      <Dialog open={rejectEsclarecOpen} onOpenChange={setRejectEsclarecOpen}>
        <DialogContent data-testid="reject-esclarec-dialog">
          <DialogHeader>
            <DialogTitle>Pedir mais informação</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              O contrato volta ao estado <strong>Esclarecimentos</strong> e o comercial recebe uma nova notificação.
            </p>
            <div>
              <Label>Motivo / informação adicional necessária <span className="text-rose-500">*</span></Label>
              <Textarea
                value={rejectEsclarecText}
                onChange={(e) => setRejectEsclarecText(e.target.value)}
                rows={4}
                placeholder="A resposta não esclarece o ponto X. Pede para confirmar Y..."
                data-testid="reject-esclarec-input"
              />
              <p className="text-xs text-slate-400 mt-1">Mínimo 10 caracteres ({rejectEsclarecText.trim().length}/10)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectEsclarecOpen(false)} disabled={validatingEsclarec}>Cancelar</Button>
            <Button
              onClick={() => handleValidateEsclarecimentos('reject')}
              disabled={validatingEsclarec || rejectEsclarecText.trim().length < 10}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-reject-esclarec"
            >
              {validatingEsclarec ? 'A enviar...' : 'Pedir mais info'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Corrigir Comercializadora dialog */}
      <Dialog open={fixCompanyOpen} onOpenChange={setFixCompanyOpen}>
        <DialogContent data-testid="fix-company-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-amber-600" /> Corrigir Comercializadora
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Corrige rapidamente a empresa do contrato e/ou a comercializadora anterior. A correcção fica registada no Fluxo do contrato.
            </p>
            <div>
              <Label>Empresa Atual *</Label>
              <Select value={fixCompany} onValueChange={setFixCompany}>
                <SelectTrigger data-testid="fix-company-select"><SelectValue placeholder="Escolher empresa..." /></SelectTrigger>
                <SelectContent>
                  {fixCompanyOptions.map(c => (
                    <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1">Atual: <strong>{contract?.company}</strong></p>
            </div>
            <div>
              <Label>Comercializadora Anterior</Label>
              <Select value={fixComercAnt || '__none__'} onValueChange={(v) => setFixComercAnt(v === '__none__' ? '' : v)}>
                <SelectTrigger data-testid="fix-comerc-ant-select"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sem definir —</SelectItem>
                  <SelectItem value="primeira_contratacao">Primeira contratação</SelectItem>
                  <SelectItem value="outra">Não sei / Outra</SelectItem>
                  {fixCompanyOptions.map(c => (
                    <SelectItem key={`pa-${c.slug}`} value={c.slug}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1">Atual: <strong>{(contract?.comercializadora_anterior || '∅').toLowerCase()}</strong></p>
            </div>
            <div>
              <Label>Motivo da correcção *</Label>
              <Textarea
                value={fixMotivo}
                onChange={(e) => setFixMotivo(e.target.value)}
                rows={3}
                placeholder="Ex: O comercial selecionou Iberdrola por engano, na verdade é EDP..."
                data-testid="fix-motivo-textarea"
              />
              <p className={`text-xs mt-1 ${fixMotivo.trim().length < 10 ? 'text-rose-600' : 'text-emerald-600'}`}>
                {fixMotivo.trim().length}/10 caracteres mínimos
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFixCompanyOpen(false)} disabled={fixSaving}>Cancelar</Button>
            <Button
              onClick={handleFixCompanySubmit}
              disabled={fixSaving || fixMotivo.trim().length < 10}
              className="bg-amber-600 hover:bg-amber-700 gap-2"
              data-testid="fix-company-submit"
            >
              {fixSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Corrigir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
